import { api } from "../../api/client";

export type Classification = "unclassified" | "restricted" | "secret";
export type CaseStatus = "open" | "in_review" | "closed";

// Threat-to enum is shared across cases / intel / POI / performance / popularity.
// "none" means it's not a threat to anyone — stays out of the threat report.
export type ThreatTo =
  | "none"
  | "party"
  | "president"
  | "mp"
  | "party_member"
  | "branch"
  | "ward"
  | "constituency"
  | "other";

export const THREAT_TO_LABELS: Record<ThreatTo, string> = {
  none: "Not a threat",
  party: "The Party",
  president: "President",
  mp: "MP",
  party_member: "Party member",
  branch: "Branch",
  ward: "Ward",
  constituency: "Constituency",
  other: "Other",
};

export type ResolutionStatus = "pending" | "in_progress" | "resolved" | "false_alarm";

// Cases now subsume what used to be intel feed items. `kind` discriminates:
// `investigation` is the original case-file model; `observation` / `alert` /
// `note` cover lighter-weight items migrated from `intelItems`.
export type CaseKind = "investigation" | "observation" | "alert" | "note";
export type CaseSeverity = "low" | "medium" | "high" | "critical";

export const CASE_KIND_LABELS: Record<CaseKind, string> = {
  investigation: "Investigation",
  observation: "Observation",
  alert: "Alert",
  note: "Note",
};

export interface Case {
  id: string;
  kind?: CaseKind;
  severity?: CaseSeverity | null;
  title: string;
  summary: string;
  classification: Classification;
  status: CaseStatus;
  branchId: string;
  wardId: string | null;
  constituencyId: string | null;
  pollingStationId?: string | null;
  subjectName: string | null;
  subjectIdNumber: string | null;
  subjectPhone: string | null;
  threatTo?: ThreatTo;
  threatTarget?: string | null;
  threatDetails?: string | null;
  resolutionStatus?: ResolutionStatus;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  resolutionNotes?: string | null;
  redactedAt?: string | null;
  redactedBy?: string | null;
  redactionReason?: string | null;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string | null;
  closedAt?: string | null;
}

interface List<T> { items: T[]; }

export interface CaseCreate {
  kind?: CaseKind;
  severity?: CaseSeverity;
  title: string;
  summary?: string;
  classification?: Classification;
  branchId: string;
  pollingStationId?: string;
  subjectName?: string;
  subjectIdNumber?: string;
  subjectPhone?: string;
  threatTo?: ThreatTo;
  threatTarget?: string;
  threatDetails?: string;
  // Intelligence metadata: how many independent sources contributed (≥0)
  // and the officer's numeric confidence (0–100). Optional for back-compat.
  sourcesCount?: number;
  confidenceScore?: number;
}

export const CasesApi = {
  list: (filters?: { status?: CaseStatus; branchId?: string; kind?: CaseKind | CaseKind[] }) => {
    const qs = new URLSearchParams();
    if (filters?.status) qs.set("status", filters.status);
    if (filters?.branchId) qs.set("branchId", filters.branchId);
    if (filters?.kind) {
      const k = Array.isArray(filters.kind) ? filters.kind.join(",") : filters.kind;
      qs.set("kind", k);
    }
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return api<List<Case>>(`/cases${suffix}`);
  },
  get: (id: string) => api<Case>(`/cases/${id}`),
  create: (body: CaseCreate) =>
    api<Case>("/cases", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Omit<Case, "id">>) =>
    api<Case>(`/cases/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  remove: (id: string) =>
    api<void>(`/cases/${id}`, { method: "DELETE" }),
  redact: (id: string, reason: string) =>
    api<Case>(`/cases/${id}/redact`, { method: "POST", body: JSON.stringify({ reason }) }),
};
